import React, { useState, useEffect, useRef, useId, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import './ProductAutocomplete.css';

const formatOption = (product) =>
  `${product.name} (${product.brand}) $${parseFloat(product.price || 0).toFixed(2)}`;

/**
 * ProductAutocomplete
 * Per-row product search. Filters the already-loaded products array
 * client-side (icontains on name) — no API call per keystroke.
 */
const ProductAutocomplete = ({
  products = [],
  onSelect,
  selectedProduct = null,
  disabled = false
}) => {
  const { t } = useTranslation();
  // Stable id so the combobox input can point aria-controls at its listbox.
  const listboxId = useId();
  const [searchInput, setSearchInput] = useState(selectedProduct?.name || '');
  const [showSuggestions, setShowSuggestions] = useState(false);

  const inputRef = useRef(null);
  const suggestionsRef = useRef(null);

  const query = searchInput.trim().toLowerCase();
  const suggestions = query
    ? products.filter((product) => product.name.toLowerCase().includes(query))
    : [];

  useEffect(() => {
    setSearchInput(selectedProduct?.name || '');
  }, [selectedProduct?.name]);

  /**
   * The dropdown is positioned with `fixed` rather than `absolute` because
   * this component is used inside `.table-wrapper`, whose `overflow: auto`
   * clips absolutely positioned descendants — the menu rendered underneath the
   * grid instead of over it. Fixed escapes that clip (no ancestor sets a
   * transform), so its coordinates have to be measured off the input.
   *
   * Only geometry is written here, imperatively; every visual property stays
   * in the stylesheet, and the component declares no inline styles.
   */
  const measureDropdown = useCallback(() => {
    const input = inputRef.current;
    const panel = suggestionsRef.current;
    if (!input || !panel) return;

    const rect = input.getBoundingClientRect();
    panel.style.top = `${rect.bottom}px`;
    panel.style.left = `${rect.left}px`;
    panel.style.width = `${rect.width}px`;
  }, []);

  useEffect(() => {
    if (!showSuggestions) return undefined;

    measureDropdown();
    // `true` captures scrolls on the grid wrapper too, not just the window.
    window.addEventListener('scroll', measureDropdown, true);
    window.addEventListener('resize', measureDropdown);
    return () => {
      window.removeEventListener('scroll', measureDropdown, true);
      window.removeEventListener('resize', measureDropdown);
    };
    // suggestionCount re-measures when the panel swaps between the list and
    // the empty state, which changes which node the ref points at.
  }, [showSuggestions, suggestions.length, measureDropdown]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      const isClickOutside =
        inputRef.current && !inputRef.current.contains(event.target) &&
        suggestionsRef.current && !suggestionsRef.current.contains(event.target);

      if (isClickOutside) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleChange = (e) => {
    setSearchInput(e.target.value);
    setShowSuggestions(true);
  };

  const handleSelect = (product) => {
    setSearchInput(product.name);
    setShowSuggestions(false);
    onSelect(product);
  };

  return (
    <div className="product-search-container">
      <input
        ref={inputRef}
        type="text"
        value={searchInput}
        onChange={handleChange}
        onFocus={() => query && setShowSuggestions(true)}
        placeholder={t('clientQuotes.productSearchPlaceholder')}
        disabled={disabled}
        className="product-search-input"
        role="combobox"
        aria-label={t('clientQuotes.productSearchPlaceholder')}
        aria-autocomplete="list"
        aria-expanded={showSuggestions}
        aria-controls={listboxId}
      />

      {showSuggestions && suggestions.length > 0 && (
        <div
          ref={suggestionsRef}
          id={listboxId}
          className="product-search-suggestions"
          role="listbox"
        >
          {suggestions.map((product) => (
            <div
              key={product.id}
              onClick={() => handleSelect(product)}
              className="product-suggestion-item"
              role="option"
              aria-selected={selectedProduct?.id === product.id}
            >
              {formatOption(product)}
            </div>
          ))}
        </div>
      )}

      {showSuggestions && query && suggestions.length === 0 && (
        <div ref={suggestionsRef} className="product-search-empty" role="status">
          {t('clientQuotes.noProductsFound')}
        </div>
      )}
    </div>
  );
};

export default ProductAutocomplete;
